"use strict";
/*
 * Copyright Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.SurveyObjectsUnserializer = void 0;
const Home_1 = require("evolution-common/lib/services/baseObjects/Home");
const Household_1 = require("evolution-common/lib/services/baseObjects/Household");
const Interview_1 = require("evolution-common/lib/services/baseObjects/interview/Interview");
const SurveyObjectsRegistry_1 = require("evolution-common/lib/services/baseObjects/SurveyObjectsRegistry");
/**
 * Utility class to unserialize survey objects from their serialized form
 * This is used when receiving surveyObjectsAndAudits from the backend
 */
class SurveyObjectsUnserializer {
    /**
     * Unserialize surveyObjectsAndAudits from the backend
     * @param serializedData - The serialized survey objects payload from the backend
     * @returns Unserialized survey objects with proper object instances
     */
    static unserialize(serializedData) {
        if (!serializedData) {
            return undefined;
        }
        // clear first, especially when dealing with mulitple interviews,
        // otherwise the registry will fill out with previous interview objects
        const registry = new SurveyObjectsRegistry_1.SurveyObjectsRegistry();
        try {
            const data = serializedData;
            const result = {
                audits: data.audits || [],
                auditsByObject: data.auditsByObject || {
                    interview: [],
                    household: [],
                    home: [],
                    persons: {},
                    journeys: {},
                    visitedPlaces: {},
                    trips: {},
                    segments: {}
                },
                interview: undefined,
                household: undefined,
                home: undefined
            };
            // Unserialize Interview - now using Interview.unserialize method
            if (data.interview) {
                try {
                    const interviewData = data.interview;
                    result.interview = Interview_1.Interview.unserialize(interviewData, registry);
                }
                catch (error) {
                    console.error('Failed to unserialize interview:', error);
                }
            }
            // Unserialize Home - this will handle any nested Place objects
            if (data.home) {
                try {
                    result.home = Home_1.Home.unserialize(data.home, registry);
                }
                catch (error) {
                    console.error('Failed to unserialize home:', error);
                }
            }
            // Unserialize Household - this will automatically handle all nested objects:
            // - members (persons) -> journeys -> visitedPlaces, trips -> segments
            if (data.household) {
                try {
                    const householdData = data.household;
                    result.household = Household_1.Household.unserialize(householdData, registry);
                }
                catch (error) {
                    console.error('Failed to unserialize household:', error);
                }
            }
            return result;
        }
        catch (error) {
            console.error('Failed to unserialize surveyObjectsAndAudits:', error);
            return undefined;
        }
    }
    /**
     * Check if surveyObjectsAndAudits data is present and valid
     * @param data - The data to check
     * @returns boolean indicating if data is present
     */
    static hasValidData(data) {
        if (!data || typeof data !== 'object') {
            return false;
        }
        const obj = data;
        return !!(obj.interview || obj.household || obj.home || (Array.isArray(obj.audits) && obj.audits.length > 0));
    }
    /**
     * Set up parent-child UUID relationships for unserialized objects
     * Note: Objects register themselves in the registry via their constructors
     * @param surveyObjects - The unserialized survey objects
     */
    static registerObjectsInRegistry(surveyObjects) {
        // Set up household parent UUIDs
        if (surveyObjects.household) {
            if (surveyObjects.interview) {
                surveyObjects.household.interviewUuid = surveyObjects.interview._uuid;
            }
            if (surveyObjects.home) {
                surveyObjects.household.homeUuid = surveyObjects.home._uuid;
            }
            // Set up person parent UUIDs
            if (surveyObjects.household.members) {
                for (const person of surveyObjects.household.members) {
                    person.householdUuid = surveyObjects.household._uuid;
                    // Set up journey parent UUIDs
                    if (person.journeys) {
                        for (const journey of person.journeys) {
                            journey.personUuid = person._uuid;
                            // Set up visited place parent UUIDs
                            if (journey.visitedPlaces) {
                                for (const visitedPlace of journey.visitedPlaces) {
                                    visitedPlace.journeyUuid = journey._uuid;
                                }
                            }
                            // Set up trip parent UUIDs
                            if (journey.trips) {
                                for (const trip of journey.trips) {
                                    trip.journeyUuid = journey._uuid;
                                    // Set up segment parent UUIDs
                                    if (trip.segments) {
                                        for (const segment of trip.segments) {
                                            segment.tripUuid = trip._uuid;
                                        }
                                    }
                                    // Set up junction parent UUIDs
                                    if (trip.junctions) {
                                        for (const junction of trip.junctions) {
                                            junction.tripUuid = trip._uuid;
                                        }
                                    }
                                }
                            }
                            // Set up trip chain parent UUIDs
                            if (journey.tripChains) {
                                for (const tripChain of journey.tripChains) {
                                    tripChain.journeyUuid = journey._uuid;
                                }
                            }
                        }
                    }
                    // Set up vehicle parent UUIDs
                    if (person.vehicles) {
                        for (const vehicle of person.vehicles) {
                            vehicle.ownerUuid = person._uuid;
                        }
                    }
                }
            }
        }
    }
}
exports.SurveyObjectsUnserializer = SurveyObjectsUnserializer;
//# sourceMappingURL=SurveyObjectsUnserializer.js.map